import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';
import { downloadChartAsPNG, formatFilename } from '../../utils/downloadUtils';
import './EMFACCountyVMTChart.css';

Chart.register(...registerables);

interface EMFACCountyVMTChartProps {
  location: string;
  geofips: number | null;
  onDownloadPNGReady?: (handler: () => void) => void;
}

interface VMTDataPoint {
  year: number;
  emfac2021_ldv: number;
  emfac2025_ldv: number;
}

export const EMFACCountyVMTChart: React.FC<EMFACCountyVMTChartProps> = ({ location, geofips, onDownloadPNGReady }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<VMTDataPoint[]>([]);
  const [countyName, setCountyName] = useState<string>('');

  const handleDownloadPNG = useCallback(() => {
    const filename = formatFilename(location, 'EMFAC_County_LDV_VMT');
    downloadChartAsPNG(chartRef, filename);
  }, [location]);

  useEffect(() => {
    if (onDownloadPNGReady && !loading && !error && data.length > 0) {
      onDownloadPNGReady(handleDownloadPNG);
    }
  }, [onDownloadPNGReady, handleDownloadPNG, loading, error, data.length]);

  useEffect(() => {
    const fetchData = async () => {
      if (!location || !geofips) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Get location info to determine if it's a city or county
        const { data: locationData, error: locationError } = await supabaseClient
          .from('population')
          .select('location_name, geography_type, county')
          .eq('geofips', geofips)
          .limit(1)
          .single();

        if (locationError || !locationData) {
          throw new Error('Could not find location data');
        }

        // Determine county geofips
        let countyGeofips = geofips;
        let countyLocationName = locationData.location_name;

        if (locationData.geography_type === 'City') {
          // For cities, find the county geofips
          const countyName = locationData.county || `${locationData.location_name} County`;
          
          const { data: countyData, error: countyError } = await supabaseClient
            .from('population')
            .select('geofips, location_name')
            .eq('location_name', countyName)
            .eq('geography_type', 'County')
            .limit(1)
            .single();

          if (countyError || !countyData) {
            throw new Error(`Could not find county data for ${countyName}`);
          }

          countyGeofips = countyData.geofips;
          countyLocationName = countyData.location_name;
        }

        setCountyName(countyLocationName);

        console.log('🔍 EMFAC County VMT: Fetching data for county geofips:', countyGeofips, countyLocationName);

        // Fetch EMFAC2021 data (emfac2021_county) - LDV only
        const { data: emfac2021Data, error: emfac2021Error } = await supabaseClient
          .from('emfac2021_county')
          .select('year, class, total_vmt')
          .eq('geofips', countyGeofips)
          .eq('class', 'LDV')
          .gte('year', 2000)
          .lte('year', 2050)
          .order('year', { ascending: true });

        if (emfac2021Error) {
          console.error('Error fetching EMFAC2021 data:', emfac2021Error);
          throw emfac2021Error;
        }

        // Fetch EMFAC2025 data (emfac2025_county) - LDV only
        const { data: emfac2025Data, error: emfac2025Error } = await supabaseClient
          .from('emfac2025_county')
          .select('year, class, total_vmt')
          .eq('geofips', countyGeofips)
          .eq('class', 'LDV')
          .gte('year', 2000)
          .lte('year', 2050)
          .order('year', { ascending: true });

        if (emfac2025Error) {
          console.error('Error fetching EMFAC2025 data:', emfac2025Error);
          throw emfac2025Error;
        }

        console.log('📊 EMFAC County VMT: EMFAC2021 records:', emfac2021Data?.length || 0);
        console.log('📊 EMFAC County VMT: EMFAC2025 records:', emfac2025Data?.length || 0);

        // Aggregate data by year and class
        const yearMap = new Map<number, VMTDataPoint>();

        // Process EMFAC2021 data (LDV only)
        (emfac2021Data || []).forEach(row => {
          const year = row.year;
          const vmt = row.total_vmt || 0;

          if (!yearMap.has(year)) {
            yearMap.set(year, {
              year,
              emfac2021_ldv: 0,
              emfac2025_ldv: 0
            });
          }

          const point = yearMap.get(year)!;
          point.emfac2021_ldv += vmt;
        });

        // Process EMFAC2025 data (LDV only)
        (emfac2025Data || []).forEach(row => {
          const year = row.year;
          const vmt = row.total_vmt || 0;

          if (!yearMap.has(year)) {
            yearMap.set(year, {
              year,
              emfac2021_ldv: 0,
              emfac2025_ldv: 0
            });
          }

          const point = yearMap.get(year)!;
          point.emfac2025_ldv += vmt;
        });

        // Convert to array and sort by year
        const processedData = Array.from(yearMap.values())
          .sort((a, b) => a.year - b.year);

        console.log('📊 EMFAC County VMT: Processed data points:', processedData.length);
        console.log('📊 EMFAC County VMT: Sample data:', processedData.slice(0, 3));

        setData(processedData);
        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching EMFAC County VMT data:', err);
        setError(err.message || 'Failed to load EMFAC County VMT data');
        setLoading(false);
      }
    };

    fetchData();
  }, [location, geofips]);

  useEffect(() => {
    if (data.length === 0 || !chartRef.current) return;

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    // Destroy existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Create new chart
    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: data.map(d => d.year.toString()),
        datasets: [
          {
            label: 'EMFAC2021 LDV',
            data: data.map(d => d.emfac2021_ldv),
            borderColor: 'rgb(156, 163, 175)',
            backgroundColor: 'rgba(156, 163, 175, 0.1)',
            fill: false,
            tension: 0.4,
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            borderDash: [5, 5],
            order: 2
          },
          {
            label: 'EMFAC2025 LDV',
            data: data.map(d => d.emfac2025_ldv),
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: false,
            tension: 0.4,
            borderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            order: 1
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: standardLegend,
          title: standardTitle(`County-Level LDV VMT - ${countyName}`),
          tooltip: {
            callbacks: {
              label: function(context) {
                const value = context.parsed.y;
                return `${context.dataset.label}: ${(value ?? 0).toLocaleString()} miles`;
              }
            }
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: 'Year'
            }
          },
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'VMT (miles)'
            },
            ticks: {
              callback: function(value) {
                return value != null && typeof value === 'number' ? value.toLocaleString() : '';
              }
            }
          }
        }
      }
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data, countyName]);

  if (loading) {
    return <div className="emfac-county-vmt-loading">Loading EMFAC County VMT data...</div>;
  }

  if (error) {
    return <div className="emfac-county-vmt-error">{error}</div>;
  }

  if (data.length === 0) {
    return <div className="emfac-county-vmt-no-data">No EMFAC County VMT data available</div>;
  }

  return (
    <div className="emfac-county-vmt-chart-container">
      <canvas ref={chartRef}></canvas>
    </div>
  );
};

